import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card } from '@timely/ui';
import { supabase } from '../lib/supabaseClient';
import { useWorkspaces } from '../workspace/WorkspaceProvider';
import { useSession } from '../auth/SessionProvider';
import { AuthLayout, Field, FormNotice } from './AuthLayout';
import {
    CURRENCY_CHOICES,
    DEFAULT_WORKSPACE,
    LOCALE_CHOICES,
    TIMEZONE_CHOICES,
} from '../config/defaults';


/**
 * Creating a company.
 *
 * One call to `create_workspace()` (migration 0004), which does the whole thing
 * in one transaction: the company, its five roles, the caller as owner, its
 * settings, its onboarding state and a neutral category list. Either all of it
 * exists or none of it does — a company with no owner cannot be administered by
 * anyone and there is no support path in to fix it, by design.
 *
 * The function also refuses an unverified account and rate-limits creation, so
 * this screen does not have to be the place those rules are enforced.
 */
export function CreateCompany() {
    const navigate = useNavigate();
    const { reload, rememberSlug } = useWorkspaces();
    const { email } = useSession();

    const [name, setName] = useState('');
    const [currency, setCurrency] = useState<string>(DEFAULT_WORKSPACE.currency);
    const [timezone, setTimezone] = useState<string>(DEFAULT_WORKSPACE.timezone);
    const [locale, setLocale] = useState<string>(DEFAULT_WORKSPACE.locale);
    const [phoneCountry, setPhoneCountry] = useState<string>(DEFAULT_WORKSPACE.phoneCountry);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        setError(null);
        if (!name.trim()) {
            setError('Your company needs a name.');
            return;
        }

        setBusy(true);
        const { data: workspaceId, error: rpcError } = await supabase().rpc('create_workspace', {
            p_name: name.trim(),
            p_country: DEFAULT_WORKSPACE.country,
            p_currency: currency,
            p_timezone: timezone,
            p_locale: locale,
            p_phone_country: phoneCountry,
        });
        setBusy(false);

        if (rpcError) {
            setError(
                /verify your email/i.test(rpcError.message)
                    ? 'Confirm your email address first, then create your company.'
                    : /too many companies/i.test(rpcError.message)
                        ? 'You have created several companies today. Try again tomorrow.'
                        : rpcError.message || 'We could not create the company. Try again.',
            );
            return;
        }

        // The function hands back the id it created; the slug is generated
        // server-side, so it is read rather than guessed.
        const { data: created } = await supabase()
            .from('workspaces')
            .select('slug')
            .eq('id', workspaceId as string)
            .maybeSingle();

        await reload();
        if (created?.slug) {
            rememberSlug(created.slug);
            navigate(`/w/${created.slug}/today`, { replace: true });
        } else {
            navigate('/', { replace: true });
        }
    };

    return (
        <AuthLayout
            title="Set up your company"
            lede={`Signed in as ${email ?? 'you'}. This is the company your team will work in.`}
        >
            <form onSubmit={submit} noValidate>
                {error && <FormNotice tone="error">{error}</FormNotice>}

                <Field
                    label="Company name"
                    name="company"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                    autoFocus
                    hint="Shown beneath Timely Payment throughout the app."
                />

                <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
                    <Select label="Currency" value={currency} onChange={setCurrency} options={CURRENCY_CHOICES} />
                    <Select label="Locale" value={locale} onChange={setLocale} options={LOCALE_CHOICES} />
                    <Select label="Time zone" value={timezone} onChange={setTimezone} options={TIMEZONE_CHOICES} />
                    <Field
                        label="Phone country code"
                        name="phone"
                        inputMode="numeric"
                        value={phoneCountry}
                        onChange={e => setPhoneCountry(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                    />
                </div>

                <p className="text-[12.5px] text-label-3 leading-snug mt-1 mb-5">
                    All of these can be changed later in settings. They decide how money and dates
                    are shown, and when your daily reminder goes out.
                </p>

                <Button type="submit" variant="primary" className="w-full" disabled={busy}>
                    {busy ? 'Creating…' : 'Create company'}
                </Button>
            </form>
        </AuthLayout>
    );
}

function Select({
    label, value, onChange, options,
}: { label: string; value: string; onChange: (v: string) => void; options: readonly string[] }) {
    const id = label.toLowerCase().replace(/\s+/g, '-');
    return (
        <div className="mb-4">
            <label htmlFor={id} className="block text-[13px] font-semibold text-label mb-1.5">{label}</label>
            <select
                id={id}
                value={value}
                onChange={e => onChange(e.target.value)}
                className="w-full h-11 px-3 rounded-[11px] bg-card border border-separator-strong text-[15px] text-label outline-none focus:border-accent"
            >
                {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}

/**
 * Where somebody lands when they belong to more than one company and have not
 * said which. Only reachable when there is a real choice to make.
 */
export function ChooseCompany({ contexts }: { contexts: { workspace: { name: string; slug: string } }[] }) {
    const navigate = useNavigate();
    const { rememberSlug } = useWorkspaces();
    return (
        <main className="min-h-dvh flex items-center justify-center px-4 py-10">
            <div className="w-full max-w-[420px]">
                <h1 className="text-[22px] font-bold text-label tracking-[-0.02em] mb-1">
                    Which company?
                </h1>
                <p className="text-[14px] text-label-2 mb-6">You belong to more than one.</p>
                <Card className="divide-y divide-separator overflow-hidden">
                    {contexts.map(c => (
                        <button
                            key={c.workspace.slug}
                            type="button"
                            onClick={() => {
                                rememberSlug(c.workspace.slug);
                                navigate(`/w/${c.workspace.slug}/today`);
                            }}
                            className="w-full text-left px-5 py-4 min-h-[56px] hover:bg-hover transition-colors"
                        >
                            <span className="font-semibold text-label text-[15px]">{c.workspace.name}</span>
                        </button>
                    ))}
                </Card>
            </div>
        </main>
    );
}
